import { CombinedGraphQLErrors, CombinedProtocolErrors, type ErrorLike } from '@apollo/client'
import type { FieldError } from 'api-graphql-types'
import type { GraphQLFormattedError } from 'graphql'
import type { FieldErrors } from 'react-hook-form'
import { ZodError } from 'zod'
import type { $ZodIssue } from 'zod/v4/core'
import { ErrorTypes } from './ErrorTypes'
import { CommonError } from './types'
import AppError, { type IAppError } from './AppError'
import { Translator } from '@/i18n/types'
const BASE_PATH = 'base'

export type ServerPathMapper = (serverPath: string) => string[]

function assertNoSymbol(input: PropertyKey[]) {
  for (const key of input) {
    if (typeof key !== 'symbol') continue
    throw new Error('Symbols as keys are not supported in forms!')
  }
  return input as Exclude<PropertyKey, symbol>[]
}

export function fromZodIssue<T = unknown>(issue: $ZodIssue): CommonError<T> {
  const path = assertNoSymbol(issue.path)
  if (path[0] === 'root') {
    return {
      message: issue.message,
      type: ErrorTypes.FormError,
    }
  }
  return {
    path,
    message: issue.message,
    type: ErrorTypes.FieldError,
    errorType: issue.code as T,
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function fromError<T = unknown>(error: any, t: Translator<'nextjs'>): CommonError<T> {
  if (isUnauthorizedError(error)) {
    return {
      type: ErrorTypes.UnauthorizedError,
      message: t('errors.messages.unauthorized'),
    }
  }
  if ('message' in error) {
    return {
      message: t('errors.messages.general'),
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      original_message: error.message,
      type: ErrorTypes.GeneralError,
    }
  }
  return {
    type: ErrorTypes.UnknownError,
  }
}

export function fromApolloErrorToErrors(error: ErrorLike, t: Translator<'nextjs'>): CommonError[] {
  if (CombinedGraphQLErrors.is(error)) {
    return error.errors.map((error) => fromGraphQLError(error, t))
  }
  if (CombinedProtocolErrors.is(error)) {
    return error.errors.map((error) => fromGraphQLError(error, t))
  }
  return [fromError(error, t)]
}

export function fromGraphQLError<T = unknown>(error: GraphQLFormattedError, t: Translator<'nextjs'>): CommonError<T> {
  return {
    type: ErrorTypes.GeneralError,
    message: t('errors.messages.general'),
    original_message: error.message,
  }
}

export function fromFieldError<T = unknown>(error: FieldError, mapPaths?: ServerPathMapper): CommonError<T> {
  if (error.field === BASE_PATH) {
    return {
      type: ErrorTypes.FormError,
      message: error.message,
    }
  }
  const paths = mapPaths?.(error.field) ?? [error.field]
  return {
    type: ErrorTypes.FieldError,
    path: paths,
    message: error.message,
    errorType: (error.errorType as T) ?? undefined,
  }
}

export function toErrorObject<T extends Record<string | number, unknown>, V = unknown>(
  errors: Array<CommonError<V>>
): FieldErrors<T> {
  function getErrorPath(error: CommonError<V>) {
    return 'path' in error ? error.path : ['root']
  }
  function inner(errors: Array<CommonError<V>>, depth = 0) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const errorObject: Record<any, any> = {}
    for (const error of errors) {
      if (!('message' in error)) {
        continue
      }
      const path = getErrorPath(error)
      const field = path[depth]!
      const lastLevel = path.length - 1 - depth < 1
      const currentPath = path.slice(0, depth + 1).join('.')
      if (!lastLevel) {
        const nestedErrors = errors.filter((err) => getErrorPath(err).join('.').startsWith(currentPath))
        errorObject[field] = inner(nestedErrors, depth + 1)
        continue
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      errorObject[field] = errorObject[field] ?? {
        message: error.message,
        type: 'errorType' in error ? error.errorType : undefined,
      }
    }
    return errorObject
  }
  return inner(errors) as FieldErrors<T>
}

export function fromErrorObject<V = unknown>(
  errors: Record<string, unknown>,
  parentPath: string[] = []
): Array<CommonError<V>> {
  const result: Array<CommonError<unknown>> = []

  for (const [key, value] of Object.entries(errors)) {
    const currentPath = [...parentPath, key]

    if (value && typeof value === 'object' && 'message' in value && typeof value.message === 'string') {
      // This is a leaf node with an error
      const generalError = !key || key === 'root'
      if (generalError) {
        result.push({
          type: ErrorTypes.GeneralError,
          message: value.message,
        })
        continue
      }
      const error: CommonError = {
        type: ErrorTypes.FieldError,
        path: currentPath,
        message: value.message,
      }
      if ('type' in value) {
        error['errorType'] = value.type
      }
      result.push(error)
    } else if (value && typeof value === 'object') {
      // This is a nested error object
      result.push(...fromErrorObject(value as Record<string, unknown>, currentPath))
    }
  }

  return result as Array<CommonError<V>>
}

function isErrorLike(err: unknown): err is ErrorLike {
  return !!err && typeof err === 'object' && 'message' in err
}

export function isNetworkError(err: unknown): err is { statusCode: number } {
  return isErrorLike(err) && 'statusCode' in err && typeof err.statusCode === 'number'
}

export function isRedirectError(err: unknown): err is { message: 'NEXT_REDIRECT' } {
  return isErrorLike(err) && err.message === 'NEXT_REDIRECT'
}

export function isNotFoundError(err: unknown): err is { message: 'NEXT_NOT_FOUND' | 'NEXT_HTTP_ERROR_FALLBACK;404' } {
  const message = isErrorLike(err) && err.message
  return message === 'NEXT_NOT_FOUND' || message === 'NEXT_HTTP_ERROR_FALLBACK;404'
}

export function isUnauthenticatedError(err: unknown): err is { statusCode: 401 } {
  return isNetworkError(err) && err.statusCode === 401
}

export function isUnauthorizedError(err: unknown): err is { statusCode: 403 } {
  return isNetworkError(err) && err.statusCode === 403
}

export function isZodError(err: unknown): err is ZodError {
  return err instanceof ZodError
}

export function isAppError(err: unknown): err is AppError {
  return err instanceof AppError
}

export function isIAppError(err: unknown): err is IAppError {
  return !!err && typeof err === 'object' && '__typename' in err && err.__typename === 'AppError'
}
